/**
 * usePageTracking Hook
 *
 * Automatically tracks page views when the route changes.
 * Should be used once at the app root level.
 */

import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, initializeAnalytics } from '@/services/analyticsService';

interface UsePageTrackingOptions {
  /** Whether to enable page tracking (default: true) */
  enabled?: boolean;
}

export const usePageTracking = (options: UsePageTrackingOptions = {}): void => {
  const { enabled = true } = options;
  const location = useLocation();
  const isInitializedRef = React.useRef(false);

  // Initialize analytics on first render
  React.useEffect(() => {
    if (!isInitializedRef.current) {
      initializeAnalytics();
      isInitializedRef.current = true;
    }
  }, []);

  // Track page views on route changes
  React.useEffect(() => {
    if (!enabled) return;

    // Get page title from document or generate from path
    const getPageTitle = (path: string): string => {
      const titles: Record<string, string> = {
        '/': 'Home',
        '/optimizer': 'Optimizer',
        '/inventory': 'Inventory Intelligence',
        '/verification': 'Performance Verification',
        '/portfolio-health': 'Portfolio Health',
        '/advertising-health': 'Advertising Health',
        '/inventory-health': 'Inventory Health',
        '/catalog-health': 'Catalog Health',
        '/pricing-optimizer': 'Pricing Optimizer',
        '/promotions': 'Promotions Analysis',
        '/reporting': 'P&L Reporting',
        '/help': 'Help Center',
        '/methodology': 'Methodology',
        '/glossary': 'Glossary',
        '/faq': 'FAQ',
        '/login': 'Login',
        '/signup': 'Sign Up',
        '/pricing': 'Pricing',
        '/settings/profile': 'Profile Settings',
        '/settings/subscription': 'Subscription Management',
        '/checkout': 'Checkout',
        '/checkout/success': 'Checkout Success',
        '/checkout/canceled': 'Checkout Canceled',
      };

      return titles[path] || document.title;
    };

    const title = getPageTitle(location.pathname);
    trackPageView(location.pathname, title);
  }, [enabled, location.pathname]);
};

export default usePageTracking;
